import { useState, useEffect, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import toast from 'react-hot-toast';
import { useAuth } from '@/stores/auth';
import {
  legalService,
  MATTER_TYPES,
  CONFIDENTIALITY_LEVELS,
  MATTER_TYPE_LABELS,
  CONFIDENTIALITY_LABELS,
} from '../services/legal.service';

const inputCls =
  'w-full rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500';
const labelCls = 'text-sm font-medium text-secondary-700 dark:text-secondary-300';

interface FormData {
  title: string;
  type: string;
  subType: string;
  priority: string;
  confidentiality: string;
  leadAttorneyName: string;
  leadAttorneyId: string;
  summary: string;
  dateOfIncident: string;
  statuteOfLimitations: string;
  expectedResolution: string;
  budgetAmount: string;
  budgetCurrency: string;
  tags: string[];
}

const INITIAL_FORM: FormData = {
  title: '',
  type: 'LITIGATION_PLAINTIFF',
  subType: '',
  priority: 'MEDIUM',
  confidentiality: 'STANDARD',
  leadAttorneyName: '',
  leadAttorneyId: '',
  summary: '',
  dateOfIncident: '',
  statuteOfLimitations: '',
  expectedResolution: '',
  budgetAmount: '',
  budgetCurrency: 'USD',
  tags: [],
};

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
] as const;

const PRIORITY_BUTTON_COLORS: Record<string, string> = {
  LOW: 'border-success-500 bg-success-50 text-success-700 dark:border-success-600 dark:bg-success-900/20 dark:text-success-300',
  MEDIUM: 'border-secondary-400 bg-secondary-50 text-secondary-700 dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-300',
  HIGH: 'border-warning-500 bg-warning-50 text-warning-700 dark:border-warning-600 dark:bg-warning-900/20 dark:text-warning-300',
  CRITICAL: 'border-danger-500 bg-danger-50 text-danger-700 dark:border-danger-600 dark:bg-danger-900/20 dark:text-danger-300',
};

export default function CreateMatter() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEditing = Boolean(id);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (!id) {return;}
    let cancelled = false;
    setLoading(true);
    legalService
      .getMatter(id)
      .then((matter) => {
        if (cancelled) {return;}
        setFormData({
          title: matter.title ?? '',
          type: matter.type ?? 'LITIGATION_PLAINTIFF',
          subType: matter.subType ?? '',
          priority: matter.priority ?? 'MEDIUM',
          confidentiality: matter.confidentiality ?? 'STANDARD',
          leadAttorneyName: matter.leadAttorneyName ?? '',
          leadAttorneyId: matter.leadAttorneyId ?? '',
          summary: matter.summary ?? '',
          dateOfIncident: matter.dateOfIncident
            ? matter.dateOfIncident.slice(0, 16)
            : '',
          statuteOfLimitations: matter.statuteOfLimitations
            ? matter.statuteOfLimitations.slice(0, 16)
            : '',
          expectedResolution: matter.expectedResolution
            ? matter.expectedResolution.slice(0, 16)
            : '',
          budgetAmount: matter.budgetAmount ? String(matter.budgetAmount) : '',
          budgetCurrency: matter.budgetCurrency ?? 'USD',
          tags: Array.isArray(matter.tags) ? matter.tags : [],
        });
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : 'Failed to load matter');
          navigate('/legal/matters');
        }
      })
      .finally(() => {
        if (!cancelled) {setLoading(false);}
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setFormData((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.leadAttorneyName.trim()) {
      toast.error('Lead attorney name is required');
      return;
    }

    try {
      setSubmitting(true);

      const payload: Record<string, unknown> = {
        title: formData.title.trim(),
        type: formData.type,
        priority: formData.priority,
        confidentiality: formData.confidentiality,
        leadAttorneyId: formData.leadAttorneyId.trim() !== '' ? formData.leadAttorneyId.trim() : (user?.id ?? 'unknown'),
        leadAttorneyName: formData.leadAttorneyName.trim(),
        budgetCurrency: formData.budgetCurrency,
        tags: formData.tags,
      };

      if (formData.subType.trim()) {payload.subType = formData.subType.trim();}
      if (formData.summary.trim()) {payload.summary = formData.summary.trim();}
      if (formData.dateOfIncident) {payload.dateOfIncident = new Date(formData.dateOfIncident).toISOString();}
      if (formData.statuteOfLimitations) {payload.statuteOfLimitations = new Date(formData.statuteOfLimitations).toISOString();}
      if (formData.expectedResolution) {payload.expectedResolution = new Date(formData.expectedResolution).toISOString();}
      if (formData.budgetAmount) {payload.budgetAmount = parseFloat(formData.budgetAmount);}

      if (isEditing && id) {
        await legalService.updateMatter(id, payload);
        toast.success('Matter updated successfully');
        navigate(`/legal/matters/${id}`);
      } else {
        const newMatter = await legalService.createMatter(payload);
        toast.success('Matter created successfully');
        navigate(`/legal/matters/${newMatter.id}`);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : isEditing
          ? 'Failed to update matter'
          : 'Failed to create matter';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-center text-secondary-600 dark:text-secondary-400 py-12">
          Loading matter...
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate(isEditing ? `/legal/matters/${id}` : '/legal/matters')}
          className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
        </button>
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
          {isEditing ? 'Edit Matter' : 'New Legal Matter'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="p-6 space-y-8">
            {/* Core Information */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Core Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="title" className={labelCls}>
                    Title <span className="text-danger-500">*</span>
                  </label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="Enter matter title"
                    className="mt-1"
                    maxLength={300}
                  />
                </div>
                <div>
                  <label htmlFor="type" className={labelCls}>
                    Type
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className={cn(inputCls, 'mt-1')}
                  >
                    {MATTER_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {MATTER_TYPE_LABELS[t] ?? t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="subType" className={labelCls}>
                    Sub-Type
                  </label>
                  <Input
                    id="subType"
                    name="subType"
                    value={formData.subType}
                    onChange={handleChange}
                    placeholder="Optional sub-type"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="confidentiality" className={labelCls}>
                    Confidentiality
                  </label>
                  <select
                    id="confidentiality"
                    name="confidentiality"
                    value={formData.confidentiality}
                    onChange={handleChange}
                    className={cn(inputCls, 'mt-1')}
                  >
                    {CONFIDENTIALITY_LEVELS.map((c) => (
                      <option key={c} value={c}>
                        {CONFIDENTIALITY_LABELS[c] ?? c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Priority */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Priority
              </h2>
              <div className="flex flex-wrap gap-3">
                {PRIORITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, priority: opt.value }))}
                    className={cn(
                      'px-5 py-2 rounded-lg border-2 text-sm font-medium transition-all',
                      formData.priority === opt.value
                        ? PRIORITY_BUTTON_COLORS[opt.value]
                        : 'border-secondary-300 dark:border-secondary-600 text-secondary-600 dark:text-secondary-400 hover:border-secondary-400'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Lead Attorney */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Lead Attorney
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="leadAttorneyName" className={labelCls}>
                    Lead Attorney Name <span className="text-danger-500">*</span>
                  </label>
                  <Input
                    id="leadAttorneyName"
                    name="leadAttorneyName"
                    value={formData.leadAttorneyName}
                    onChange={handleChange}
                    required
                    placeholder="Full name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="leadAttorneyId" className={labelCls}>
                    Lead Attorney User ID (optional)
                  </label>
                  <Input
                    id="leadAttorneyId"
                    name="leadAttorneyId"
                    value={formData.leadAttorneyId}
                    onChange={handleChange}
                    placeholder="System user ID"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Summary */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Summary
              </h2>
              <div>
                <label htmlFor="summary" className={labelCls}>
                  Matter Summary
                </label>
                <textarea
                  id="summary"
                  name="summary"
                  value={formData.summary}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Describe the matter..."
                  className={cn(inputCls, 'mt-1')}
                />
              </div>
            </div>

            {/* Key Dates */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Key Dates
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <DateTimeInput
                    id="dateOfIncident"
                    label="Date of Incident"
                    value={formData.dateOfIncident}
                    onChange={(value) => setFormData((prev) => ({ ...prev, dateOfIncident: value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <DateTimeInput
                    id="statuteOfLimitations"
                    label="Statute of Limitations"
                    value={formData.statuteOfLimitations}
                    onChange={(value) => setFormData((prev) => ({ ...prev, statuteOfLimitations: value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <DateTimeInput
                    id="expectedResolution"
                    label="Expected Resolution"
                    value={formData.expectedResolution}
                    onChange={(value) => setFormData((prev) => ({ ...prev, expectedResolution: value }))}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Budget */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Budget
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="budgetAmount" className={labelCls}>
                    Budget Amount
                  </label>
                  <Input
                    id="budgetAmount"
                    name="budgetAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.budgetAmount}
                    onChange={handleChange}
                    placeholder="0.00"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="budgetCurrency" className={labelCls}>
                    Currency
                  </label>
                  <Input
                    id="budgetCurrency"
                    name="budgetCurrency"
                    value={formData.budgetCurrency}
                    onChange={handleChange}
                    placeholder="USD"
                    maxLength={3}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Tags */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Tags
              </h2>
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-300 rounded-full text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-1 hover:text-primary-600"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Add a tag and press Enter"
                  className={cn(inputCls, 'flex-1')}
                />
                <Button type="button" variant="outline" onClick={addTag}>
                  Add
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(isEditing ? `/legal/matters/${id}` : '/legal/matters')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting
                  ? isEditing
                    ? 'Saving...'
                    : 'Creating...'
                  : isEditing
                  ? 'Save Changes'
                  : 'Create Matter'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
